import { createSelector } from '../selectorWrapper'

import { imagesSelector, liveStateSelector } from '../detailFragment'
import { pageSelectors, simpleSelectors } from '../basicState'
import {
  sessionIdSelector,
  isRetailSelector,
  marketplaceIdSelector,
  urlMakerSelector,
  MakeUrl
} from '../requestContext'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { isGen4, isTVSelector } from '../shared'
import { TitleIdToDetail } from '../../reducers/detail'
import { TitleIdToSelf } from '../../reducers/self'
import { isJPMarketplace } from '../../utils/marketplace'
import { playbackFirstChildSelector } from '../actionFragment'
import { multiplePlaybackComponentSelector, MultiplePlaybackData } from './actionBoxComponentSelectors'
import { SeasonInfo } from '../../reducers/seasons'

export const titleComponentSelector = createSelector(
  [urlMakerSelector, isRetailSelector, pageSelectors.headerDetail],
  (makeUrl: MakeUrl, isRetail: boolean, detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps) => {
    if (detailFragment) {
      return {
        title: detailFragment.parentTitle || detailFragment.title,
        asin: detailFragment.asin,
        cGti: detailFragment.compactGti,
        makeUrl,
        isRetail
      }
    }
    return undefined
  }
)

export const metaDataSelector = createSelector(
  [isRetailSelector, isTVSelector, pageSelectors.headerDetail],
  (
    isRetail: boolean,
    isTV: boolean,
    detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps
  ) => {
    if (detailFragment) {
      return { ...detailFragment, isRetail, isTV }
    }
    return undefined
  }
)

export const synopsisSelector = createSelector(
  pageSelectors.headerDetail,
  (detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps) =>
    (detailFragment && { synopsis: detailFragment.synopsis }) || undefined
)

export const liveStateDataSelector = createSelector(
  liveStateSelector,
  (liveState?: WpsRuntime.atv.wps.LiveStateProps) =>
    (liveState && {
      text: liveState.text,
      isLive: liveState.isLive
    } || undefined)
)

export const seasonsSelector = createSelector(
  [
    simpleSelectors.detail,
    simpleSelectors.self,
    pageSelectors.seasons,
    simpleSelectors.pageTitleId,
    isRetailSelector
  ],
  (
    details: TitleIdToDetail,
    self: TitleIdToSelf,
    seasons?: SeasonInfo[],
    pageTitleId?: WpsRuntime.atv.wps.TitleID,
    isRetail?: boolean
  ) => ({
    seasons: seasons
      ? seasons.map(season => ({
          titleId: season.seasonId,
          seasonLink: season.seasonLink,
          detail: details[season.seasonId],
          self: self[season.seasonId]
        }))
      : undefined,
    pageTitleId,
    isRetail
  })
)

export const heroShotSelector = createSelector(
  [
    sessionIdSelector,
    simpleSelectors.pageTitleId,
    isRetailSelector,
    isTVSelector,
    marketplaceIdSelector,
    imagesSelector,
    playbackFirstChildSelector
  ],
  (
    sessionId: string,
    pageTitleId: string,
    isRetail: boolean,
    isTV: boolean,
    marketplaceId: string,
    images?: WpsRuntime.atv.wps.ImagesProps,
    playbackData?: WpsRuntime.atv.wps.PlaybackActionDataProps) =>
    (images && {
      heroshot: !isJPMarketplace(marketplaceId) && images.heroshot || undefined, // Force no heroshot in jp
      packshot: images.packshot,
      sessionId,
      pageTitleId,
      isRetail,
      isTV,
      playbackData
    }) || undefined
)

export const circlePlaybackSelector = createSelector(
  [
    sessionIdSelector,
    simpleSelectors.pageTitleId,
    isRetailSelector,
    isTVSelector,
    marketplaceIdSelector,
    imagesSelector,
    playbackFirstChildSelector
  ],
  (
    sessionId: string,
    pageTitleId: string,
    isRetail: boolean,
    isTV: boolean,
    marketplaceId,
    images?: WpsRuntime.atv.wps.ImagesProps,
    playbackData?: WpsRuntime.atv.wps.PlaybackActionDataProps
  ) => ({
    sessionId,
    pageTitleId,
    isRetail,
    isTV,
    isPackshot: isJPMarketplace(marketplaceId) || !Boolean(images && images.heroshot), // packshot always in JP or when no heroshot
    playbackData
  })
)

// Returns true if the atf has minimum data needed to render, false otherwise
export const atfCriticalDataSelector = createSelector(
  [
    isGen4,
    pageSelectors.headerDetail,
    multiplePlaybackComponentSelector,
  ],
  (
    isGen4: boolean,
    detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps,
    playbackActions?: MultiplePlaybackData,
  ) => {
    const hasTitle = Boolean(detailFragment && detailFragment.title)
    const hasPlayback = Boolean(playbackActions)

    return isGen4 ?
      hasTitle || hasPlayback :
      hasTitle
  }
)

export const renderOnlyGen4PlaybackInAtfSelector = createSelector(
  [
    isGen4,
    pageSelectors.headerDetail,
    multiplePlaybackComponentSelector,
  ],
  (
    isGen4: boolean,
    detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps,
    playbackActions?: MultiplePlaybackData,
  ) => {
    const missingTitle = !Boolean(detailFragment && detailFragment.title)
    const hasPlayback = Boolean(playbackActions)
    return isGen4 && missingTitle && hasPlayback
  }
)
